'use client'

import { useEffect, useRef } from 'react'

export const ScrollReveal = ({
  children,
  animationClass,
  delayMs = 0,
  threshold = 0,
  rootMargin = '0px 0px -20% 0px',
  className = '',
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const animationClasses = animationClass
      ? animationClass.split(' ').filter(Boolean)
      : []

    if (!mediaQuery.matches && animationClasses.length > 0) {
      element.classList.add('opacity-0')
    }

    if (mediaQuery.matches || typeof IntersectionObserver === 'undefined') {
      element.classList.remove('opacity-0')
      return
    }

    const reveal = () => {
      element.classList.remove('opacity-0')

      const preset = getAnimationPreset(animationClass)
      if (preset) {
        element.animate(preset.keyframes, {
          duration: preset.durationMs,
          easing: preset.easing,
          fill: 'forwards',
          delay: delayMs,
        })
        return
      }

      if (delayMs > 0) {
        element.style.animationDelay = `${delayMs}ms`
      }

      animationClasses.forEach((name) => element.classList.add(name))
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          reveal()
          observer.unobserve(element)
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [animationClass, delayMs, rootMargin, threshold])

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  )
}

const getAnimationPreset = (
  animationClass?: string
): {
  keyframes: Keyframe[]
  durationMs: number
  easing: string
} | null => {
  if (!animationClass) {
    return null
  }

  if (animationClass.includes('animate-zoomIn')) {
    return {
      keyframes: [
        { opacity: 0, transform: 'scale(0.8)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      durationMs: 500,
      easing: 'ease-out',
    }
  }

  if (animationClass.includes('animate-fadeInDown')) {
    return {
      keyframes: [
        { opacity: 0, transform: 'translateY(-20px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      durationMs: 500,
      easing: 'ease-out',
    }
  }

  if (animationClass.includes('animate-flipInY')) {
    return {
      keyframes: [
        { opacity: 0, transform: 'rotateY(90deg)' },
        { opacity: 1, transform: 'rotateY(0deg)' },
      ],
      durationMs: 500,
      easing: 'ease-out',
    }
  }

  if (animationClass.includes('animate-fadeIn')) {
    return {
      keyframes: [{ opacity: 0 }, { opacity: 1 }],
      durationMs: 500,
      easing: 'ease-out',
    }
  }

  return null
}

interface ScrollRevealProps {
  children: React.ReactNode
  animationClass?: string
  delayMs?: number
  threshold?: number
  rootMargin?: string
  className?: string
}
